import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { catchError, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { AnnouncementsService } from '../announcements.service'
import {
  ArchiveMyAnnouncement,
  DeactivateAnnouncement,
  DeleteMyAnnouncement,
  GetAdminAnnouncementDetail,
  GetAdminAnnouncements,
  GetAnnouncementUsers,
  GetMyAnnouncements,
  GetUnreadAnnouncementsCount,
  MarkAnnouncementRead,
  MarkAnnouncementUnread,
  SendAnnouncement,
  UnarchiveMyAnnouncement
} from './announcements.actions'
import { AnnouncementsStateModel } from './announcements.model'
import { IAnnouncementItem } from '../../interfaces/announcement.interface'

@State<AnnouncementsStateModel>({
  name: 'announcements'
})
@Injectable()
export class AnnouncementsState {
  constructor(private announcementsService: AnnouncementsService) {}

  @Selector()
  static announcements(state: AnnouncementsStateModel) {
    return state.announcements
  }

  @Selector()
  static unreadCount(state: AnnouncementsStateModel) {
    return state.unreadCount
  }

  @Selector()
  static loudAnnouncements(state: AnnouncementsStateModel) {
    return state.loudAnnouncements
  }

  @Selector()
  static users(state: AnnouncementsStateModel) {
    return state.users
  }

  @Selector()
  static adminListResponse(state: AnnouncementsStateModel) {
    return state.adminListResponse
  }

  @Selector()
  static adminDetail(state: AnnouncementsStateModel) {
    return state.adminDetail
  }

  @Action(GetMyAnnouncements)
  getMyAnnouncements(ctx: StateContext<AnnouncementsStateModel>) {
    return this.announcementsService.getMyAnnouncements().pipe(
      tap((res) => {
        ctx.patchState({
          announcements: res?.items ?? [],
          unreadCount: res?.unreadCount ?? 0
        })
      })
    )
  }

  @Action(GetUnreadAnnouncementsCount)
  getUnreadAnnouncementsCount(ctx: StateContext<AnnouncementsStateModel>) {
    return this.announcementsService.getUnreadCount().pipe(
      tap((res) => {
        ctx.patchState({
          unreadCount: res?.count ?? 0,
          loudAnnouncements: res?.loudItems ?? []
        })
      }),
      catchError(() => of(null))
    )
  }

  // An item counts toward the unread badge iff it is unread and not archived.
  private static counted(item?: IAnnouncementItem): boolean {
    return !!item && !item.readDate && !item.archivedDate
  }

  // Patches (or removes) one announcement locally and adjusts unreadCount by the
  // resulting delta — the count cannot be recomputed from the list because the
  // list caps at 100 while the server-side count does not.
  // The item may live only in loudAnnouncements (popup closed on a page where the
  // "mine" list was never fetched); loudAnnouncements mirrors the server's pending-popup
  // list, so any item that stops being counted is dropped from it here as well.
  private patchAnnouncement(
    ctx: StateContext<AnnouncementsStateModel>,
    recipientId: string,
    change: Partial<IAnnouncementItem> | 'remove'
  ) {
    const state = ctx.getState()
    const before =
      state.announcements?.find((q) => q.recipientId === recipientId) ??
      state.loudAnnouncements?.find((q) => q.recipientId === recipientId)
    if (!before) {
      return
    }
    const after = change === 'remove' ? undefined : { ...before, ...change }
    const delta =
      (AnnouncementsState.counted(after) ? 1 : 0) - (AnnouncementsState.counted(before) ? 1 : 0)
    ctx.patchState({
      announcements: after
        ? state.announcements?.map((q) => (q.recipientId === recipientId ? after : q))
        : state.announcements?.filter((q) => q.recipientId !== recipientId),
      loudAnnouncements: AnnouncementsState.counted(after)
        ? state.loudAnnouncements
        : state.loudAnnouncements?.filter((q) => q.recipientId !== recipientId),
      unreadCount: Math.max((state.unreadCount ?? 0) + delta, 0)
    })
  }

  @Action(MarkAnnouncementRead)
  markAnnouncementRead(
    ctx: StateContext<AnnouncementsStateModel>,
    { recipientId }: MarkAnnouncementRead
  ) {
    return this.announcementsService
      .markRead(recipientId)
      .pipe(tap(() => this.patchAnnouncement(ctx, recipientId, { readDate: new Date().toISOString() })))
  }

  @Action(MarkAnnouncementUnread)
  markAnnouncementUnread(
    ctx: StateContext<AnnouncementsStateModel>,
    { recipientId }: MarkAnnouncementUnread
  ) {
    return this.announcementsService
      .markUnread(recipientId)
      .pipe(tap(() => this.patchAnnouncement(ctx, recipientId, { readDate: undefined })))
  }

  @Action(ArchiveMyAnnouncement)
  archiveMyAnnouncement(
    ctx: StateContext<AnnouncementsStateModel>,
    { recipientId }: ArchiveMyAnnouncement
  ) {
    return this.announcementsService
      .archiveMine(recipientId)
      .pipe(
        tap(() => this.patchAnnouncement(ctx, recipientId, { archivedDate: new Date().toISOString() }))
      )
  }

  @Action(UnarchiveMyAnnouncement)
  unarchiveMyAnnouncement(
    ctx: StateContext<AnnouncementsStateModel>,
    { recipientId }: UnarchiveMyAnnouncement
  ) {
    return this.announcementsService
      .unarchiveMine(recipientId)
      .pipe(tap(() => this.patchAnnouncement(ctx, recipientId, { archivedDate: undefined })))
  }

  @Action(DeleteMyAnnouncement)
  deleteMyAnnouncement(
    ctx: StateContext<AnnouncementsStateModel>,
    { recipientId }: DeleteMyAnnouncement
  ) {
    return this.announcementsService
      .deleteMine(recipientId)
      .pipe(tap(() => this.patchAnnouncement(ctx, recipientId, 'remove')))
  }

  @Action(SendAnnouncement)
  sendAnnouncement(ctx: StateContext<AnnouncementsStateModel>, { payload }: SendAnnouncement) {
    return this.announcementsService.sendAnnouncement(payload)
  }

  @Action(GetAnnouncementUsers)
  getAnnouncementUsers(ctx: StateContext<AnnouncementsStateModel>) {
    return this.announcementsService.getUsers().pipe(
      tap((res) => {
        ctx.patchState({
          users: res?.items ?? []
        })
      })
    )
  }

  @Action(GetAdminAnnouncements)
  getAdminAnnouncements(ctx: StateContext<AnnouncementsStateModel>, { payload }: GetAdminAnnouncements) {
    return this.announcementsService.getAdminList(payload).pipe(
      tap((res) => {
        ctx.patchState({
          adminListResponse: res
        })
      })
    )
  }

  @Action(GetAdminAnnouncementDetail)
  getAdminAnnouncementDetail(
    ctx: StateContext<AnnouncementsStateModel>,
    { id }: GetAdminAnnouncementDetail
  ) {
    ctx.patchState({
      adminDetail: undefined
    })
    return this.announcementsService.getAdminDetail(id).pipe(
      tap((res) => {
        ctx.patchState({
          adminDetail: res
        })
      })
    )
  }

  @Action(DeactivateAnnouncement)
  deactivateAnnouncement(ctx: StateContext<AnnouncementsStateModel>, { id }: DeactivateAnnouncement) {
    return this.announcementsService.deactivate(id)
  }
}
